import { observer } from 'mobx-react-lite';
import type { JSX, ReactNode } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { BlackbaudRaisersEdgeIntegration } from '@feathr/blackbox';
import type { IIntegrationUIToggleSection } from '@feathr/components';
import { SyncRuleSection } from '@feathr/components';

import { BLACKBAUD_ICON } from '../blackbaudIcon';
import { DONATION_UPSYNC_KEY, GIFT_DOWNSYNC_KEY } from '../raisersEdgeConfig';
import RaisersEdgeTabContent from '../RaisersEdgeTabContent';
import {
  disableToggle,
  getActivityValues,
  getToggleField,
  setDonationUpsync,
  stageDownsyncActivity,
} from './RaisersEdgeActivitiesTab.utils';
import RaisersEdgeFundSelect from './RaisersEdgeFundSelect';

interface IProps {
  integration: BlackbaudRaisersEdgeIntegration;
  /** The config's toggle sections. */
  sections: readonly IIntegrationUIToggleSection[];
  /** Disables every toggle with this reason, e.g. while the page saves. */
  disabledReason?: string;
}

function RaisersEdgeActivitiesTab({
  disabledReason,
  integration,
  sections,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const values = getActivityValues({
    downsync_activities: integration.get('downsync_activities'),
    upsync_activities: integration.get('upsync_activities'),
  });
  const shownSections = integration.get('has_provisioning_gift_subscriptions')
    ? disableToggle(
        sections,
        GIFT_DOWNSYNC_KEY,
        t('Gift sync is being set up in Blackbaud. Try again in a few minutes.'),
      )
    : sections;
  const fundField = getToggleField(sections, DONATION_UPSYNC_KEY);

  // Stage the edit on the model; the page's Save & Sync persists it. The Action toggles have no
  // attribute yet, and are disabled.
  function handleChange(key: string, value: boolean): void {
    switch (key) {
      case GIFT_DOWNSYNC_KEY:
        stageDownsyncActivity(integration, 'gift_events', value);
        break;

      case DONATION_UPSYNC_KEY:
        integration.set({
          upsync_activities: setDonationUpsync(integration.get('upsync_activities'), value),
        });
        // A fund only goes with upsync on, so turning it off drops an unsaved fund pick.
        if (!value && integration.isAttributeDirty('fundraising')) {
          integration.set({ fundraising: integration.shadowAttributes.fundraising }, false);
          integration.setAttributeClean('fundraising');
        }
        break;
    }
  }

  function renderSlot(key: string): ReactNode {
    if (key !== DONATION_UPSYNC_KEY || fundField?.type !== 'select') {
      return null;
    }
    return (
      <RaisersEdgeFundSelect
        disabledReason={disabledReason}
        enabled={!!values[DONATION_UPSYNC_KEY]}
        field={fundField}
        integration={integration}
      />
    );
  }

  return (
    <RaisersEdgeTabContent
      inactiveLabel={t('Connect Blackbaud to choose which activities sync.')}
      integration={integration}
    >
      {shownSections.map((section) => (
        <SyncRuleSection
          crmIcon={BLACKBAUD_ICON}
          disabledReason={disabledReason}
          key={section.key}
          name={`raisers-edge-${section.key}`}
          onChange={handleChange}
          renderSlot={renderSlot}
          section={section}
          values={values}
        />
      ))}
    </RaisersEdgeTabContent>
  );
}

export default observer(RaisersEdgeActivitiesTab);
